export type NormalizedImageUpload = {
    base64: string;
    mimeType: string;
    sizeBytes: number;
    fileName: string;
};

type LoadedImageRaster = {
    width: number;
    height: number;
    source: CanvasImageSource;
    dispose?: () => void;
};

export type ImageNormalizationEnvironment = {
    readBase64: (blob: Blob) => Promise<string>;
    loadImage: (file: Blob) => Promise<LoadedImageRaster>;
    renderToBlob: (
        image: LoadedImageRaster,
        width: number,
        height: number,
        mimeType: string,
        quality?: number
    ) => Promise<Blob | null>;
};

const resizeDimensionCandidates = [1920, 1600, 1280, 1024, 768];
const qualityCandidates = [0.9, 0.82, 0.74, 0.66, 0.58, 0.5];
const lossyMimeTypes = new Set(['image/jpeg', 'image/webp']);
const mimeExtensionMap: Record<string, string> = {
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp'
};

export async function normalizeImageForUpload(
    file: File,
    maxBytes: number,
    environmentOverrides?: Partial<ImageNormalizationEnvironment>
): Promise<NormalizedImageUpload | null> {
    if (maxBytes <= 0 || file.size <= 0) {
        return null;
    }

    const environment = createEnvironment(environmentOverrides);

    if (file.size <= maxBytes && hasKnownImageMimeType(file.type)) {
        return toNormalizedUpload(file, file.name, file.type, file.size, environment.readBase64);
    }

    const image = await environment.loadImage(file);

    try {
        const outputMimeTypes = resolveOutputMimeTypes(file.type);

        for (const maxDimension of resizeDimensionCandidates) {
            const { width, height } = fitWithinBounds(image.width, image.height, maxDimension);

            for (const mimeType of outputMimeTypes) {
                const candidateQualities = lossyMimeTypes.has(mimeType) ? qualityCandidates : [undefined];

                for (const quality of candidateQualities) {
                    const blob = await environment.renderToBlob(image, width, height, mimeType, quality);
                    if (!blob || blob.size <= 0 || blob.size > maxBytes) {
                        continue;
                    }

                    const resolvedFileName = resolveFileName(file.name, blob.type || mimeType);
                    return toNormalizedUpload(
                        blob,
                        resolvedFileName,
                        blob.type || mimeType,
                        blob.size,
                        environment.readBase64
                    );
                }
            }
        }

        return null;
    } finally {
        image.dispose?.();
    }
}

function createEnvironment(
    environmentOverrides?: Partial<ImageNormalizationEnvironment>
): ImageNormalizationEnvironment {
    return {
        readBase64: environmentOverrides?.readBase64 ?? readBlobAsBase64,
        loadImage: environmentOverrides?.loadImage ?? loadImageRaster,
        renderToBlob: environmentOverrides?.renderToBlob ?? renderImageToBlob
    };
}

function hasKnownImageMimeType(mimeType: string): boolean {
    const normalized = (mimeType ?? '').trim().toLowerCase();
    return normalized.startsWith('image/') && normalized.length > 'image/'.length;
}

function resolveOutputMimeTypes(originalMimeType: string): string[] {
    const normalized = originalMimeType.toLowerCase();
    const ordered: string[] = [];

    if (normalized === 'image/jpeg' || normalized === 'image/jpg') {
        ordered.push('image/jpeg', 'image/webp');
    } else if (normalized === 'image/webp') {
        ordered.push('image/webp', 'image/jpeg');
    } else {
        ordered.push('image/webp', 'image/jpeg', 'image/png');
    }

    return Array.from(new Set(ordered));
}

function fitWithinBounds(width: number, height: number, maxDimension: number): { width: number; height: number } {
    if (width <= 0 || height <= 0) {
        return { width: 1, height: 1 };
    }

    const largestSide = Math.max(width, height);
    if (largestSide <= maxDimension) {
        return { width, height };
    }

    const scale = maxDimension / largestSide;
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale))
    };
}

function resolveFileName(fileName: string, mimeType: string): string {
    const fallbackName = fileName.trim().length > 0 ? fileName.trim() : 'image';
    const extension = mimeExtensionMap[mimeType.toLowerCase()];
    if (!extension) {
        return fallbackName;
    }

    const dotIndex = fallbackName.lastIndexOf('.');
    if (dotIndex <= 0) {
        return `${fallbackName}${extension}`;
    }

    return `${fallbackName.slice(0, dotIndex)}${extension}`;
}

async function toNormalizedUpload(
    blob: Blob,
    fileName: string,
    mimeType: string,
    sizeBytes: number,
    readBase64: (blob: Blob) => Promise<string>
): Promise<NormalizedImageUpload> {
    return {
        base64: await readBase64(blob),
        fileName,
        mimeType,
        sizeBytes
    };
}

function readBlobAsBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === 'string' ? reader.result : '';
            const base64 = result.includes(',') ? result.split(',').pop() ?? '' : result;
            resolve(base64);
        };
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read file.'));
        reader.readAsDataURL(blob);
    });
}

async function loadImageRaster(file: Blob): Promise<LoadedImageRaster> {
    if (typeof createImageBitmap === 'function') {
        const bitmap = await createImageBitmap(file);
        return {
            width: bitmap.width,
            height: bitmap.height,
            source: bitmap,
            dispose: () => bitmap.close()
        };
    }

    const imageSource = await loadImageElement(file);
    return {
        width: imageSource.image.naturalWidth || imageSource.image.width,
        height: imageSource.image.naturalHeight || imageSource.image.height,
        source: imageSource.image,
        dispose: imageSource.dispose
    };
}

function loadImageElement(file: Blob): Promise<{ image: HTMLImageElement; dispose: () => void }> {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();

        const cleanup = () => {
            image.onload = null;
            image.onerror = null;
        };

        image.onload = () => {
            cleanup();
            resolve({
                image,
                dispose: () => URL.revokeObjectURL(objectUrl)
            });
        };

        image.onerror = () => {
            cleanup();
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Unable to decode image.'));
        };

        image.src = objectUrl;
    });
}

async function renderImageToBlob(
    image: LoadedImageRaster,
    width: number,
    height: number,
    mimeType: string,
    quality?: number
): Promise<Blob | null> {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, width);
    canvas.height = Math.max(1, height);

    const context = canvas.getContext('2d');
    if (!context) {
        return null;
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image.source, 0, 0, canvas.width, canvas.height);

    return new Promise((resolve) => {
        canvas.toBlob(
            (blob) => {
                resolve(blob);
            },
            mimeType,
            quality
        );
    });
}
